/**
 * New integration: seven steps on one page (doc 07, screen 2).
 *
 * Before step 2 is done nothing exists on the server, so the choices are kept
 * in this browser. Step 2 makes the integration with a draft; from then on the
 * draft is saved after every step, and a person can leave and come back from
 * the Integrations list, where it shows as Setting up.
 */

import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useCanWrite, useKeyed, readLocal, writeLocal } from "@/components/integrations/hooks";
import { useMappingData } from "@/components/integrations/useMappingData";
import { FieldsStage } from "@/components/integrations/stages/FieldsStage";
import { MatchStage } from "@/components/integrations/stages/MatchStage";
import { RowsStage } from "@/components/integrations/stages/RowsStage";
import { ScheduleStage } from "@/components/integrations/stages/ScheduleStage";
import { SourceStage, type ImportChoice } from "@/components/integrations/stages/SourceStage";
import { TryStage } from "@/components/integrations/stages/TryStage";
import {
  Banner,
  Button,
  Card,
  ChangeList,
  Checkbox,
  Column,
  Crumbs,
  Field,
  FieldRow,
  Input,
  PageHead,
  Panel,
  Select,
  SkeletonRows,
  Stepper,
  Toolbar,
  type StepperItem,
} from "@/components/kit";
import { ApiError, listEvents } from "@/lib/api/client";
import { getAdminApiBaseUrl } from "@/lib/api/config";
import { getWorkflow, listWorkflows } from "@/lib/api/workflows";
import {
  DEFAULT_SCOUT_BASE,
  connectDoorbell,
  createIntegration,
  getIntegration,
  getScoutJob,
  getScoutSchedule,
  listRuns,
  publishDraft,
  saveDraft,
  setScoutSchedule,
  updateIntegration,
  type IntegrationDetail,
  type ScoutJob,
} from "@/lib/integrations/api";
import { compareSettings } from "@/lib/integrations/compare";
import { cronOf, presetOf, type Preset } from "@/lib/integrations/schedule";
import { normaliseSettings, type Settings } from "@/lib/integrations/settings";
import { WIZARD_STEPS, codeFromName, furthestStep, stepProblem, type WizardState } from "@/lib/integrations/wizard";
import { when } from "@/lib/integrations/words";
import { errorText } from "@/spa/useApi";

const LOCAL = "omnium.integrations.new";

type Local = {
  base: string;
  job: ScoutJob | null;
  event: string;
  workflow: string;
  command: string;
  name: string;
  code: string;
  codeTouched: boolean;
};

export function NewIntegrationPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const code = params.get("code");
  const fromWizard = Boolean((location.state as { fromWizard?: boolean } | null)?.fromWizard);
  const detail = useKeyed(code && !fromWizard ? `detail|${code}` : null, () => getIntegration(code ?? ""));

  if (code && !fromWizard) {
    const trail = [{ label: "Integrations", href: "/integrations" }, { label: "Set up" }];
    if (detail.error) {
      const missing = detail.cause instanceof ApiError && detail.cause.status === 404;
      return (
        <>
          <Crumbs trail={trail} />
          <PageHead title="Set up an integration" />
          <Banner tone="danger" actions={missing ? <Link to="/integrations">Back to integrations</Link> : <Button onClick={detail.reload}>Try again</Button>}>
            {missing ? `There is no integration called ${code}.` : detail.error}
          </Banner>
        </>
      );
    }
    if (!detail.data) {
      return (
        <>
          <Crumbs trail={trail} />
          <PageHead title="Set up an integration" note="Loading…" />
          <Card>
            <SkeletonRows rows={5} columns={4} />
          </Card>
        </>
      );
    }
    if (detail.data.state !== "setting_up") {
      return (
        <>
          <Crumbs trail={[{ label: "Integrations", href: "/integrations" }, { label: detail.data.name }]} />
          <PageHead title={detail.data.name} />
          <Banner tone="info" actions={<Link to={`/integrations/${encodeURIComponent(detail.data.code)}/settings`}>Open its settings</Link>}>
            This integration is already set up. Change it on its Settings tab, where a draft never touches the live version.
          </Banner>
        </>
      );
    }
    return <Wizard key={code} detail={detail.data} params={params} />;
  }
  return <Wizard key="new" detail={null} params={params} />;
}

function Wizard({ detail, params }: { detail: IntegrationDetail | null; params: URLSearchParams }) {
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const [stored] = useState(() => (detail ? null : readLocal<Local>(LOCAL)));
  const commandParam = params.get("command");
  const [base, setBase] = useState(detail?.scoutBaseUrl ?? stored?.base ?? DEFAULT_SCOUT_BASE);
  const [pickedJob, setPickedJob] = useState<ScoutJob | null>(detail ? null : (stored?.job ?? null));
  const [event, setEvent] = useState(detail?.eventCode ?? params.get("event") ?? stored?.event ?? "");
  const [workflow, setWorkflow] = useState(
    detail?.workflow ?? params.get("workflow") ?? commandParam?.split(".")[0] ?? stored?.workflow ?? "",
  );
  const [command, setCommand] = useState(detail?.importCommand ?? commandParam ?? stored?.command ?? "");
  const [name, setName] = useState(detail?.name ?? stored?.name ?? "");
  const [code, setCode] = useState(detail?.code ?? stored?.code ?? "");
  const [codeTouched, setCodeTouched] = useState(Boolean(detail) || (stored?.codeTouched ?? false));
  const [current, setCurrent] = useState<IntegrationDetail | null>(detail);
  const [settings, setSettings] = useState<Settings>(() =>
    normaliseSettings(detail?.draft?.settings ?? detail?.live?.settings ?? {}),
  );
  const [saved, setSaved] = useState(() =>
    JSON.stringify(normaliseSettings(detail?.draft?.settings ?? detail?.live?.settings ?? {})),
  );
  const [at, setAt] = useState(detail ? 2 : 0);
  const [suggested, setSuggested] = useState<string[]>([]);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<{ text: string; theirs?: Settings } | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const created = current !== null;
  const integrationCode = current?.code ?? null;

  useEffect(() => {
    if (!created) writeLocal(LOCAL, { base, job: pickedJob, event, workflow, command, name, code, codeTouched } satisfies Local);
  }, [created, base, pickedJob, event, workflow, command, name, code, codeTouched]);

  const jobDetail = useKeyed(
    !pickedJob && current?.scoutJobId ? `job|${current.scoutBaseUrl}|${current.scoutJobId}` : null,
    () => getScoutJob(current?.scoutBaseUrl ?? base, current?.scoutJobId ?? ""),
  );
  const job = pickedJob ?? jobDetail.data ?? null;

  const workflows = useKeyed("workflows", () => listWorkflows());
  const events = useKeyed("events", () => listEvents());
  const applyWorkflows = (workflows.data ?? []).filter((row) => row.mode === "apply" && row.active && row.version !== null);
  const imports = useKeyed(applyWorkflows.length ? `imports|${applyWorkflows.map((row) => row.code).join(",")}` : null, () =>
    Promise.all(applyWorkflows.map((row) => getWorkflow(row.code))).then((details) =>
      details.flatMap((one) =>
        one.commands
          .filter((command) => command.imports)
          .map(
            (command): ImportChoice & { workflow: string; workflowName: string } => ({
              workflow: one.code,
              workflowName: one.name,
              command: command.code,
              label: `${one.name} · ${(command.label ?? command.code).replace(/_/g, " ")}`,
              fields: command.fields.map((field) => ({
                key: field.key,
                label: field.label ?? field.key,
                type: field.type ?? "text",
                required: Boolean(field.required),
                hint: field.hint ?? "",
                aliases: [],
                many: field.key.includes("[]"),
              })),
            }),
          ),
      ),
    ),
  );

  const data = useMappingData(integrationCode, settings);
  const runs = useKeyed(integrationCode && at >= 5 ? `runs|${integrationCode}|${current?.draftVersion}|${at}` : null, () =>
    listRuns(integrationCode ?? "", 20),
  );
  const draftSince = current?.draft?.updatedAt ?? null;
  const triedBefore = (runs.data ?? []).some(
    (run) =>
      run.via === "try" &&
      run.version === current?.draftVersion &&
      (!draftSince || (run.createdAt !== null && new Date(run.createdAt).getTime() >= new Date(draftSince).getTime() - 1000)),
  );

  const jobGate =
    job !== null
      ? { id: job.id, lastPassed: job.lastPassed }
      : created && data.sample.data?.runId
        ? {
            id: current?.scoutJobId ?? "",
            lastPassed: {
              id: data.sample.data.runId,
              status: "passed",
              rows: data.sample.data.rowsCollected,
              startedAt: data.sample.data.startedAt,
              finishedAt: null,
              error: null,
              startedBy: null,
              score: null,
            },
          }
        : null;
  const state: WizardState = {
    job: jobGate,
    event,
    command,
    code,
    name,
    created,
    settings,
    fields: data.fields.data?.fields ?? [],
    preview: data.preview.data ?? null,
    tried: tried || triedBefore,
  };
  const furthest = furthestStep(state);
  const dirty = created && JSON.stringify(settings) !== saved;
  const blocked = stepProblem(at, state);

  const saveNow = async (next: Settings = settings) => {
    if (!current) return;
    try {
      const updated = await saveDraft(current.code, { settings: next, baseUpdatedAt: current.draft?.updatedAt ?? null });
      setCurrent(updated);
      setSaved(JSON.stringify(next));
      setSavedAt(new Date().toISOString());
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 409) {
        const theirs = await getIntegration(current.code).catch(() => null);
        setProblem({ text: thrown.message, theirs: theirs ? normaliseSettings(theirs.draft?.settings ?? {}) : undefined });
        if (theirs) setCurrent({ ...current, draft: current.draft });
        throw new Error("conflict", { cause: thrown });
      }
      throw thrown;
    }
  };

  const goNext = async () => {
    setProblem(null);
    if (blocked) {
      setProblem({ text: blocked });
      return;
    }
    setBusy("next");
    try {
      if (at === 0) {
        const withJob = { ...settings, source: { ...settings.source, jobId: job?.id ?? null } };
        setSettings(withJob);
        if (!current && job && !name.trim()) {
          setName(job.name);
          if (!codeTouched) setCode(codeFromName(job.name));
        }
        if (current && job && (job.id !== current.scoutJobId || base !== current.scoutBaseUrl)) {
          setCurrent(await updateIntegration(current.code, { scoutJobId: job.id, scoutBaseUrl: base }));
        }
        if (current) await saveNow(withJob);
      } else if (at === 1) {
        if (!current) {
          const start = { ...settings, source: { jobId: job?.id ?? null, runs: "passed" as const } };
          const made = await createIntegration({
            code,
            name: name.trim(),
            event,
            importCommand: command,
            scoutBaseUrl: base,
            scoutJobId: job?.id ?? null,
            settings: start,
          });
          setCurrent(made);
          const fresh = normaliseSettings(made.draft?.settings ?? start);
          setSettings(fresh);
          setSaved(JSON.stringify(fresh));
          writeLocal(LOCAL, null);
          navigate(`/integrations/new?code=${encodeURIComponent(made.code)}`, { replace: true, state: { fromWizard: true } });
        } else if (name.trim() !== current.name) {
          setCurrent(await updateIntegration(current.code, { name: name.trim() }));
        }
      } else if (at >= 2 && at <= 4 && dirty) {
        await saveNow();
      }
      setAt(at + 1);
    } catch (thrown) {
      if (!(thrown instanceof Error && thrown.message === "conflict")) {
        setProblem({ text: errorText(thrown, "omnium did not save that") });
      }
    } finally {
      setBusy(null);
    }
  };

  const chosenImport = (imports.data ?? []).find((one) => one.command === command);
  const workflowImports = (imports.data ?? []).filter((one) => one.workflow === workflow);
  const importWorkflows = [...new Map((imports.data ?? []).map((one) => [one.workflow, one.workflowName])).entries()];
  const timeZone = current?.eventTimezone ?? "UTC";
  const disabled = !canWrite;

  const intro: Record<number, string> = {
    0: "Pick the Scout job this reads, or have Scout’s agent build one. Only runs that passed Scout’s own check are ever read.",
    1: "Which event it writes into, and which workflow command receives the rows.",
    2: "Say what one row is, and leave out rows that are not wanted — ceremonies, for example.",
    3: "Every field of the import. Pick where each value comes from and what happens to it. Press Suggest for a first guess.",
    4: "How a row finds its record, and the guards that stop a broken scrape from writing bad data.",
    5: "Run the draft on a real run. Nothing is written.",
    6: "Pick when Scout runs, then switch it on. You can also leave it off for now.",
  };

  const stepItems: StepperItem[] = WIZARD_STEPS.map((label, index) => ({
    key: String(index),
    label,
    state: index === at ? "now" : index > furthest ? "blocked" : index < furthest ? "done" : "todo",
    why: index > furthest ? stepProblem(furthest, state) : null,
  }));

  return (
    <Column>
      <Crumbs trail={[{ label: "Integrations", href: "/integrations" }, { label: current ? current.name : "New integration" }]} />
      <PageHead
        title={current ? `Set up ${current.name}` : "New integration"}
        note="Connect a Scout job to a workflow, with no code. A draft is saved after every step; you can leave and come back."
      />
      {!canWrite && <Banner tone="info">Your account cannot change integrations. You can look, but nothing can be saved.</Banner>}
      <Stepper
        items={stepItems}
        label="Set-up steps"
        progress={{ done: furthest }}
        onGo={(key) => {
          setProblem(null);
          setAt(Number(key));
        }}
      />

      <Panel title={`${at + 1}. ${WIZARD_STEPS[at]}`} note={intro[at]}>
        {problem?.theirs && (
          <Banner
            tone="warning"
            title="Someone else changed these settings."
            actions={
              <Button
                onClick={() => {
                  if (!problem.theirs || !current) return;
                  setSettings(problem.theirs);
                  setSaved(JSON.stringify(problem.theirs));
                  setProblem(null);
                  void getIntegration(current.code).then(setCurrent);
                }}
              >
                Reload their settings
              </Button>
            }
          >
            {problem.text} Nothing was overwritten. What they have that you do not:
            <ChangeList changes={compareSettings(settings, problem.theirs, Object.fromEntries((data.fields.data?.fields ?? []).map((f) => [f.key, f.label])))} />
          </Banner>
        )}
        {at === 0 && (
          <SourceStage
            base={base}
            onBase={setBase}
            job={job}
            onJob={setPickedJob}
            imports={imports.data ?? []}
            command={command}
            disabled={disabled}
          />
        )}

        {at === 1 && (
          <div style={{ display: "grid", gap: "var(--s4)" }}>
            {created && (
              <Banner tone="info">
                The event and the import cannot change once the integration exists. To send somewhere else, delete it and start again.
              </Banner>
            )}
            <FieldRow>
              <Field label="Event" required htmlFor="new-integration-event">
                <Select id="new-integration-event" value={event} disabled={created || disabled} onChange={(e) => setEvent(e.target.value)}>
                  <option value="">Pick an event</option>
                  {(events.data ?? []).map((one) => (
                    <option key={one.code} value={one.code}>
                      {one.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Workflow" required htmlFor="new-integration-workflow">
                <Select
                  id="new-integration-workflow"
                  value={workflow}
                  disabled={created || disabled}
                  onChange={(e) => {
                    setWorkflow(e.target.value);
                    setCommand("");
                  }}
                >
                  <option value="">{imports.busy && !imports.data ? "Loading…" : "Pick a workflow"}</option>
                  {importWorkflows.map(([codeOf, nameOf]) => (
                    <option key={codeOf} value={codeOf}>
                      {nameOf}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Import command" required hint="Only commands a workflow marks as imports can receive Scout rows." htmlFor="new-integration-command">
                <Select id="new-integration-command" value={command} disabled={created || disabled || !workflow} onChange={(e) => setCommand(e.target.value)}>
                  <option value="">Pick what it sends</option>
                  {workflowImports.map((one) => (
                    <option key={one.command} value={one.command}>
                      {one.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </FieldRow>
            {(imports.error || events.error) && <Banner tone="danger">{imports.error ?? events.error}</Banner>}
            {chosenImport && (
              <p style={{ margin: 0, color: "var(--ink-2)", fontSize: "var(--t-sm)" }}>
                {chosenImport.label} takes {chosenImport.fields.length} fields. Needed:{" "}
                {chosenImport.fields.filter((f) => f.required).map((f) => f.label).join(", ") || "none"}.
              </p>
            )}
            <FieldRow>
              <Field label="Name" required htmlFor="new-integration-name">
                <Input
                  id="new-integration-name"
                  value={name}
                  disabled={disabled}
                  placeholder={job ? job.name : "Fixtures — day by day"}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!codeTouched && !created) setCode(codeFromName(e.target.value));
                  }}
                />
              </Field>
              <Field label="Code" required hint="Small letters, numbers and dashes. It names the doorbell and cannot change." htmlFor="new-integration-code">
                <Input
                  id="new-integration-code"
                  value={code}
                  disabled={created || disabled}
                  onChange={(e) => {
                    setCode(e.target.value.trim());
                    setCodeTouched(true);
                  }}
                />
              </Field>
            </FieldRow>
          </div>
        )}

        {at >= 2 && at <= 4 && (!integrationCode ? null : data.sample.error ? (
          <Banner tone="warning" title="Scout is not answering." actions={<Button onClick={data.sample.reload}>Try again</Button>}>
            {data.sample.error} Settings can still be edited; previews wait for Scout.
          </Banner>
        ) : null)}

        {at === 2 && integrationCode && (
          <RowsStage
            settings={settings}
            onChange={setSettings}
            sample={data.sample.data}
            preview={data.preview.data}
            previewBusy={data.preview.busy || data.sample.busy}
            disabled={disabled}
          />
        )}

        {at === 3 && integrationCode && (data.fields.data ? (
          <FieldsStage
            code={integrationCode}
            settings={settings}
            onChange={setSettings}
            fields={data.fields.data}
            sample={data.sample.data}
            preview={data.preview.data}
            previewBusy={data.preview.busy}
            previewError={data.preview.error}
            run={data.run}
            timeZone={timeZone}
            suggested={suggested}
            onSuggested={setSuggested}
            disabled={disabled}
          />
        ) : data.fields.error ? (
          <Banner tone="danger" actions={<Button onClick={data.fields.reload}>Try again</Button>}>{data.fields.error}</Banner>
        ) : (
          <SkeletonRows rows={8} columns={5} label="Reading the import's fields" />
        ))}

        {at === 4 && integrationCode && data.fields.data && (
          <MatchStage code={integrationCode} eventCode={event} settings={settings} onChange={setSettings} fields={data.fields.data} disabled={disabled} />
        )}

        {at === 5 && current && (
          <TryStage
            code={current.code}
            versions={current.draftVersion !== null ? [{ number: current.draftVersion, label: `Draft ${current.draftVersion}` }] : []}
            version={current.draftVersion}
            safety={settings.safety}
            timeZone={timeZone}
            since={draftSince}
            onTried={() => {
              setTried(true);
              runs.reload();
            }}
          />
        )}

        {at === 6 && current && (
          <SwitchOn
            current={current}
            job={job}
            missing={data.preview.data?.missing ?? []}
            disabled={disabled}
            onDone={() => navigate(`/integrations/${encodeURIComponent(current.code)}`)}
          />
        )}

        {problem && !problem.theirs && <Banner tone="danger">{problem.text}</Banner>}

        <Toolbar>
          <Button tone="subtle" disabled={at === 0 || busy !== null} onClick={() => { setProblem(null); setAt(at - 1); }}>
            Back
          </Button>
          <span style={{ flex: 1 }} />
          {savedAt && (
            <span style={{ color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>Draft saved {when(savedAt, timeZone)}</span>
          )}
          {dirty && at >= 2 && at <= 4 && (
            <Button disabled={disabled || busy !== null} onClick={() => { setBusy("save"); saveNow().catch((thrown) => { if (!(thrown instanceof Error && thrown.message === "conflict")) setProblem({ text: errorText(thrown, "omnium did not save that") }); }).finally(() => setBusy(null)); }}>
              {busy === "save" ? "Saving…" : "Save draft"}
            </Button>
          )}
          {at < WIZARD_STEPS.length - 1 && (
            <Button tone="primary" disabled={disabled || busy !== null} onClick={() => void goNext()} title={blocked ?? undefined}>
              {busy === "next" ? "Saving…" : at === 1 && !created ? "Make it and continue" : at === 5 ? "Continue" : "Save and continue"}
            </Button>
          )}
        </Toolbar>
        {blocked && !problem && at < WIZARD_STEPS.length - 1 && (
          <p style={{ margin: 0, color: "var(--ink-2)", fontSize: "var(--t-sm)", textAlign: "right" }}>{blocked}</p>
        )}
      </Panel>
    </Column>
  );
}

function SwitchOn({
  current,
  job,
  missing,
  disabled,
  onDone,
}: {
  current: IntegrationDetail;
  job: ScoutJob | null;
  missing: string[];
  disabled: boolean;
  onDone: () => void;
}) {
  const timeZone = current.eventTimezone ?? "UTC";
  const schedule = useKeyed(`schedule|${current.code}`, () => getScoutSchedule(current.code));
  const [preset, setPreset] = useState<Preset | null>(null);
  const shown = preset ?? presetOf(schedule.data?.timing ?? null);
  const [check, setCheck] = useState(current.checkSeconds);
  const [bell, setBell] = useState(true);
  const [address, setAddress] = useState(getAdminApiBaseUrl());
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const others = (job?.usedBy ?? []).filter((one) => one !== current.code);

  const go = async () => {
    setWorking(true);
    setProblem(null);
    const said: string[] = [];
    try {
      if (current.draftVersion !== null) {
        await publishDraft(current.code, "Switched on from set-up");
        said.push(`Published version ${current.draftVersion}.`);
        setDone([...said]);
      }
      const cron = preset ? cronOf(preset) : null;
      if (cron) {
        await setScoutSchedule(current.code, cron, timeZone);
        said.push("Scout’s schedule is set.");
        setDone([...said]);
      }
      await updateIntegration(current.code, { checkSeconds: check, state: "on" });
      said.push("Switched on.");
      setDone([...said]);
      if (bell) {
        await connectDoorbell(current.code, address);
        said.push("The doorbell is connected: Scout rings omnium when a run passes.");
        setDone([...said]);
      }
      onDone();
    } catch (thrown) {
      setProblem(`${errorText(thrown, "That did not finish")} ${said.length ? `Done so far: ${said.join(" ")}` : "Nothing was changed."}`);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: "var(--s5)" }}>
      {missing.length > 0 && (
        <Banner tone="warning">These fields must come from somewhere before it can go live: {missing.join(", ")}.</Banner>
      )}
      <div style={{ display: "grid", gap: "var(--s3)" }}>
        <strong>When Scout runs the job</strong>
        <ScheduleStage
          preset={shown}
          onPreset={setPreset}
          checkSeconds={check}
          onCheckSeconds={setCheck}
          timeZone={timeZone}
          sharedWith={others}
          scoutProblem={schedule.error}
        />
      </div>
      <div style={{ display: "grid", gap: "var(--s3)" }}>
        <strong>The doorbell</strong>
        <Checkbox label="Connect the doorbell, so Scout tells omnium the moment a run passes" checked={bell} disabled={disabled} onChange={(e) => setBell(e.target.checked)} />
        {bell && (
          <FieldRow>
            <Field label="The address Scout can reach omnium on" hint="Scout calls this address; it must be reachable from where Scout runs." htmlFor="new-integration-bell">
              <Input id="new-integration-bell" value={address} disabled={disabled} onChange={(e) => setAddress(e.target.value.trim())} />
            </Field>
          </FieldRow>
        )}
      </div>
      {done.length > 0 && (
        <Banner tone="good">
          <ChangeList changes={done} />
        </Banner>
      )}
      {problem && <Banner tone="danger">{problem}</Banner>}
      {confirming ? (
        <Banner
          tone="warning"
          actions={
            <>
              <Button tone="primary" disabled={working} onClick={() => void go()}>
                {working ? "Switching on…" : "Yes, switch it on"}
              </Button>
              <Button tone="subtle" disabled={working} onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </>
          }
        >
          This publishes the draft{preset && cronOf(preset) ? ", sets Scout’s schedule" : ""} and starts writing Scout runs into {current.eventName}.
        </Banner>
      ) : (
        <Toolbar>
          <Button tone="primary" disabled={disabled || missing.length > 0} onClick={() => setConfirming(true)}>
            Switch on
          </Button>
          <Button tone="subtle" onClick={onDone}>
            Leave it off for now
          </Button>
        </Toolbar>
      )}
    </div>
  );
}
